/* Password rules for the administrator account (F1). Keep in step with the
   Frappe password policy on the site: the backend stays the authority and
   its rejection is still shown if the two ever disagree. */

import { useEffect, useState } from "react";
import { callOperation } from "@/lib/f360-client";

export type PasswordRule = { id: string; label: string; test: (value: string) => boolean };

export const PASSWORD_RULES: readonly PasswordRule[] = [
  { id: "length", label: "At least 8 characters", test: (value) => value.length >= 8 },
  { id: "upper", label: "An uppercase letter", test: (value) => /\p{Lu}/u.test(value) },
  { id: "lower", label: "A lowercase letter", test: (value) => /\p{Ll}/u.test(value) },
  { id: "digit", label: "A number", test: (value) => /\d/.test(value) },
  { id: "symbol", label: "A symbol, such as ! @ # or $", test: (value) => /[^\p{L}\d\s]/u.test(value) },
];

export const meetsPasswordPolicy = (value: string) => PASSWORD_RULES.every((rule) => rule.test(value));

/* "Hard to guess" is Frappe's zxcvbn score, which only the backend knows.
   Checked once the local rules pass, debounced, so the checklist turns
   fully green only for a password account creation will accept. When the
   check can't run, the row is hidden and the backend decides on submit. */

export type PasswordStrength = "idle" | "checking" | "strong" | "weak" | "unavailable";

export function usePasswordStrength(password: string, userInputs: readonly string[] = []): PasswordStrength {
  // Only the verdict is kept: the password itself never goes into state.
  const [strength, setStrength] = useState<PasswordStrength>("idle");
  const inputsKey = userInputs.map((value) => value.trim()).filter(Boolean).join("\n");
  const ready = meetsPasswordPolicy(password);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    setStrength("checking");
    const timer = window.setTimeout(async () => {
      try {
        const data = await callOperation<{ passed?: boolean }>("auth.password-strength", {
          new_password: password,
          ...(inputsKey ? { user_data: inputsKey.split("\n").slice(0, 5) } : {}),
        });
        const passed = data?.passed === true;
        if (!cancelled) setStrength(passed ? "strong" : "weak");
      } catch {
        if (!cancelled) setStrength("unavailable");
      }
    }, 400);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [password, inputsKey, ready]);

  return ready ? strength : "idle";
}
